import React, { useCallback, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { skipToken } from '@reduxjs/toolkit/query';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { IoMdLock } from 'react-icons/io';
import { MdOutlineAudioFile, MdOndemandVideo } from 'react-icons/md';
import { AiOutlineNotification } from 'react-icons/ai';
import { LuSticker, LuPhoneCall } from 'react-icons/lu';
import { TiDelete } from 'react-icons/ti';
import { useGetMessageMediaQuery } from '@@/services/whatsapp';
import { useViewDocumentQuery } from '@@/services/document';
import { ChatTypes } from '@@/constants/whatsapp';
import { getExtensionFromMimetype, base64ToBlob } from '@@/utils/file';
import { formatWhatsAppText } from '@@/utils/whatsapp';

const Message: React.FC<{
  message: Unpatient.ChatMessage;
  disableLinks?: boolean;
}> = ({ message, disableLinks }) => {
  const chatId = message?.id?.remote;
  const messageId = message?.id?.id;
  const documentId: string | undefined = message?._data?.documentId;
  const mimetype: string | undefined = message?._data?.mimetype;
  const filename: string | undefined = message?._data?.filename;

  // Once the API has uploaded the full media to S3 it surfaces a
  // `documentId` (either on the chat-history payload or via the
  // `msg-media` socket event). Prefer that path — it streams the bytes
  // directly from S3 instead of base64-inlining them in a JSON response.
  const { data: documentBlobUrl } = useViewDocumentQuery(
    documentId ?? skipToken,
  );

  const { data: media } = useGetMessageMediaQuery(
    { chatId: chatId, messageId },
    {
      skip:
        !!documentId ||
        !chatId ||
        !messageId ||
        ![
          ChatTypes.IMAGE,
          ChatTypes.DOCUMENT,
          ChatTypes.VIDEO,
          ChatTypes.PTT,
        ].includes(message?.type as ChatTypes),
    },
  );

  // The S3 blob URL (`documentBlobUrl`) is owned by the RTK Query cache and
  // shared across components — its lifetime is managed in `viewDocument`.
  // Only the URL we mint locally from the legacy base64 payload needs a
  // cleanup hook to avoid leaking object URLs as messages scroll.
  const localBlobUrl = useMemo(() => {
    if (documentBlobUrl || !media) {
      return undefined;
    }
    const blob = base64ToBlob(media.data, media.mimetype);
    return URL.createObjectURL(blob);
  }, [documentBlobUrl, media]);

  useEffect(
    () => () => {
      if (localBlobUrl) {
        URL.revokeObjectURL(localBlobUrl);
      }
    },
    [localBlobUrl],
  );

  const mediaURL = documentBlobUrl ?? localBlobUrl;
  const mediaMimetype = mimetype ?? media?.mimetype;
  const mediaFilename = filename ?? media?.filename;
  const mediaReady = !!mediaURL;

  const downloadMedia = useCallback(() => {
    if (disableLinks) {
      return;
    }

    if (!mediaURL) {
      toast.error(
        'Il semble que ce document ne soit pas disponible via whatsapp',
        {
          position: 'top-right',
        },
      );
      return;
    }

    const link = document.createElement('a');
    link.href = mediaURL;
    link.setAttribute(
      'download',
      mediaFilename ||
        `${messageId}.${getExtensionFromMimetype(mediaMimetype || '')}`,
    );
    document.body.appendChild(link);
    link.click();
  }, [disableLinks, mediaFilename, mediaMimetype, mediaURL, messageId]);

  if (!message?.type) {
    return <p className="italic text-yellow-400">Aucun message</p>;
  }

  switch (message?.type) {
    case ChatTypes.CHAT:
      return (
        <div
          dangerouslySetInnerHTML={{ __html: formatWhatsAppText(message.body) }}
        />
      );

    case ChatTypes.REVOKED:
      return (
        <div className="flex flex-row items-center">
          <TiDelete />
          <p>Message supprimé</p>
        </div>
      );

    case ChatTypes.IMAGE: {
      const caption = message._data.caption;
      const inlinePreview = message?._data?.body;
      const previewSrc =
        mediaURL ??
        (mimetype && inlinePreview
          ? `data:${mimetype};base64,${inlinePreview}`
          : undefined);

      return (
        <div>
          <button onClick={downloadMedia} disabled={!mediaReady}>
            {previewSrc && (
              <img className="object-cover" src={previewSrc} alt="image" />
            )}
          </button>

          {!!caption && (
            <div
              className="mt-1"
              dangerouslySetInnerHTML={{
                __html: formatWhatsAppText(caption),
              }}
            />
          )}
        </div>
      );
    }

    case ChatTypes.DOCUMENT: {
      const caption = message._data.caption;
      const displayName = mediaFilename || 'Fichier inconnu';

      return (
        <div className="">
          <button
            className="flex flex-row items-center"
            onClick={downloadMedia}
          >
            <IoDocumentTextOutline size={40} />
            <span className="ml-1 underline">{displayName}</span>
          </button>
          {!!caption && (
            <div
              className="mt-1"
              dangerouslySetInnerHTML={{
                __html: formatWhatsAppText(caption),
              }}
            />
          )}
        </div>
      );
    }

    case ChatTypes.PTT: {
      return (
        <div className="flex flex-row items-center">
          <MdOutlineAudioFile size={30} className="mr-1" />
          {!!mediaURL && !!mediaMimetype && (
            <audio controls>
              <source src={mediaURL} type={mediaMimetype} />
              Your browser does not support the audio element.
            </audio>
          )}
        </div>
      );
    }

    case ChatTypes.STICKER:
      return (
        <div className="flex flex-row items-center">
          <LuSticker size={30} />
          <span>Emoji (todo)</span>
        </div>
      );

    case ChatTypes.VCARD:
      return <p>{message.body}</p>;

    case ChatTypes.VIDEO: {
      return (
        <div>
          <div className="flex flex-row items-center mb-2">
            <MdOndemandVideo size={30} className="mr-1" />
            <span>
              Message video {!mediaReady ? ' (Contenu non disponible)' : ''}
            </span>
          </div>
          <video
            controls
            className="w-96"
            poster={
              mimetype && message._data?.body
                ? `data:${mimetype};base64,${message._data.body}`
                : undefined
            }
            src={mediaURL}
          >
            Votre navigateur ne supporte pas la balise vidéo.
          </video>
        </div>
      );
    }

    case ChatTypes.GP2:
      return <p>{message._data.body}</p>;

    case ChatTypes.CIPHERTEXT:
      return (
        <div className="flex flex-row items-center">
          <IoMdLock />
          <p>Message chiffré</p>
        </div>
      );

    case ChatTypes.NOTIFICATION_TEMPLATE:
      return (
        <div className="flex flex-row items-center">
          <AiOutlineNotification />
          <p className="mx-1">{message._data.subtype}</p>
          <AiOutlineNotification />
        </div>
      );

    case ChatTypes.CALL_LOG: {
      if (message.fromMe) {
        return (
          <div className="flex flex-row items-center">
            <LuPhoneCall size={20} />
            <span className="ml-2">
              {message._data.callOutcome === 'Missed'
                ? 'Appel émis resté sans réponse'
                : 'Appel émis et décroché'}
            </span>
          </div>
        );
      }
      return (
        <div className="flex flex-row items-center">
          <LuPhoneCall size={20} />
          <span className="ml-2">
            {message._data.callOutcome === 'Missed'
              ? 'Appel Manqué'
              : 'Appel Reçu'}
          </span>
        </div>
      );
    }

    default: {
      return <p>MESSAGE NON RECONNU</p>;
    }
  }
};

export default Message;
